import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { AlertCircle, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, ChevronsUpDown, ChevronUp, FileText, Lock, Plus, Save, X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import { useAuthStore } from "@/store/auth";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DatePicker } from "@/components/ui/DatePicker";
import { formatAmount } from "@/utils/formatAmount";
import { formatDate } from "@/utils/formatDate";
import { naturalSortKey } from "@/utils/naturalSort";
import {
  usePaginatedPlannedRevenues,
  useCreatePlannedRevenue,
  useUpdatePlannedRevenue,
  useDeletePlannedRevenue,
  useConvertPlannedRevenueToDraft,
} from "@/features/finances/hooks/useFinances";
import { useAllSubTasks } from "@/features/tasks/hooks/useSubTasks";
import { TASK_STATUS_LABELS } from "@/features/tasks/types";
import type { PlannedRevenue } from "@/features/finances/types";
import { PageLoader } from "@/components/ui/Spinner";

interface Project { id: string; name: string; short_name: string; }

// ── Detail panel (duplicated minimal version for the standalone page) ──────

interface DetailPanelProps {
  revenue: PlannedRevenue;
  projectId: string;
  canEdit: boolean;
  onClose: () => void;
}

function RevenueDetailPanel({ revenue, projectId, canEdit, onClose }: DetailPanelProps) {
  const updateMutation = useUpdatePlannedRevenue(projectId);
  const convertMutation = useConvertPlannedRevenueToDraft(projectId);
  const subtaskFilters = useMemo(() => ({ project: [projectId] }), [projectId]);
  const { data: allSubtasks = [] } = useAllSubTasks(subtaskFilters);

  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    description: revenue.description,
    notes: revenue.notes,
    amount: revenue.amount,
    expected_date: revenue.expected_date,
  });
  const [err, setErr] = useState<string | null>(null);
  const [subtaskSearch, setSubtaskSearch] = useState("");
  const [blockingIds, setBlockingIds] = useState<string[]>(revenue.blocking_subtasks.map((s) => s.id));

  const isDirty =
    form.description !== revenue.description ||
    form.notes !== revenue.notes ||
    form.amount !== revenue.amount ||
    form.expected_date !== revenue.expected_date ||
    JSON.stringify(blockingIds.slice().sort()) !==
      JSON.stringify(revenue.blocking_subtasks.map((s) => s.id).sort());

  const handleSave = async () => {
    if (!form.description.trim() || !form.amount || !form.expected_date) {
      setErr("Opis, kwota i data są wymagane.");
      return;
    }
    try {
      await updateMutation.mutateAsync({ id: revenue.id, data: { ...form, blocking_subtask_ids: blockingIds } });
      setEditMode(false);
      setErr(null);
    } catch { setErr("Błąd zapisu."); }
  };

  const [convertDialog, setConvertDialog] = useState<{ isAdvance: boolean } | null>(null);

  const handleConvertConfirm = async (isAdvance: boolean) => {
    setConvertDialog(null);
    try { await convertMutation.mutateAsync({ id: revenue.id, isAdvance }); }
    catch { setErr("Błąd tworzenia faktury."); }
  };

  const availableSubtasks = useMemo(
    () => allSubtasks.filter((s) =>
      !blockingIds.includes(s.id) &&
      (subtaskSearch === "" ||
        s.title.toLowerCase().includes(subtaskSearch.toLowerCase()) ||
        s.task_title.toLowerCase().includes(subtaskSearch.toLowerCase()))
    ),
    [allSubtasks, blockingIds, subtaskSearch]
  );
  const linkedSubtaskDetails = useMemo(() => allSubtasks.filter((s) => blockingIds.includes(s.id)), [allSubtasks, blockingIds]);

  const isAuto = revenue.is_auto_generated;
  const canModify = canEdit && !isAuto;

  return (
    <div className="fixed inset-0 z-40 flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-lg bg-white dark:bg-gray-900 h-full shadow-xl flex flex-col overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900 dark:text-gray-100 text-sm">Planowany przychód</span>
            {isAuto && <Badge color="blue">Auto</Badge>}
            {revenue.is_blocked && !isAuto && (
              <span className="inline-flex items-center gap-1 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                <Lock size={10} /> Zablokowany
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X size={18} /></button>
        </div>

        <div className="flex-1 px-5 py-4 space-y-5">
          {/* Project */}
          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Przedsięwzięcie</label>
            <p className="text-sm text-gray-700 dark:text-gray-300">{revenue.project_name}</p>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Opis</label>
            {editMode
              ? <input className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              : <p className="text-sm text-gray-900 dark:text-gray-100">{revenue.description}</p>}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Kwota netto (PLN)</label>
              {editMode
                ? <input type="number" step="0.01" className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
                : <p className="text-sm font-semibold text-green-700">+{formatAmount(revenue.amount)} PLN</p>}
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Planowana data</label>
              {editMode
                ? <DatePicker value={form.expected_date} onChange={(v) => setForm({ ...form, expected_date: v })} />
                : <p className="text-sm text-gray-700 dark:text-gray-300">{formatDate(revenue.expected_date)}</p>}
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Notatki</label>
            {editMode
              ? <textarea rows={3} className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800 resize-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Dodatkowe informacje..." />
              : revenue.notes
                ? <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">{revenue.notes}</p>
                : <p className="text-sm text-gray-400 dark:text-gray-500 italic">Brak notatek.</p>}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">Faktura PRZ</label>
            {revenue.draft_invoice_id ? (
              <Link to={`/finances/invoices-prz/${revenue.draft_invoice_id}`} className="inline-flex items-center gap-1.5 text-sm text-[var(--color-primary)] hover:underline">
                <FileText size={14} /> {revenue.draft_invoice_number || "Szkic"}
              </Link>
            ) : isAuto ? (
              <p className="text-xs text-gray-400 italic">Auto-przychody nie mogą mieć faktur.</p>
            ) : canEdit ? (
              <>
                <Button size="sm" variant="secondary" loading={convertMutation.isPending} onClick={() => setConvertDialog({ isAdvance: false })}>
                  <FileText size={14} className="mr-1" /> Utwórz szkic faktury PRZ
                </Button>
                {convertDialog && (
                  <div className="mt-2 p-3 bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] border border-[var(--color-primary)] dark:border-[var(--color-primary)] rounded-lg flex flex-col gap-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={convertDialog.isAdvance}
                        onChange={(e) => setConvertDialog({ isAdvance: e.target.checked })}
                        className="w-4 h-4 accent-[var(--color-primary)]"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">Faktura zaliczkowa</span>
                    </label>
                    <div className="flex gap-2">
                      <Button size="sm" loading={convertMutation.isPending} onClick={() => handleConvertConfirm(convertDialog.isAdvance)}>
                        Utwórz szkic
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => setConvertDialog(null)}>Anuluj</Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-xs text-gray-400 italic">Brak powiązanej faktury.</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-2">Zadania blokujące fakturowanie</label>
            {linkedSubtaskDetails.length > 0 ? (
              <div className="space-y-1 mb-3">
                {linkedSubtaskDetails.map((s) => (
                  <div key={s.id} className="flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-md px-3 py-1.5 text-xs">
                    <div className="flex items-center gap-2 min-w-0">
                      {s.status === "done"
                        ? <CheckCircle2 size={13} className="text-green-500 flex-shrink-0" />
                        : <AlertCircle size={13} className="text-amber-500 flex-shrink-0" />}
                      <div className="min-w-0">
                        <span className="text-gray-500 dark:text-gray-400">{s.task_title} / </span>
                        <span className="text-gray-800 dark:text-gray-200 font-medium">{s.title}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${s.status === "done" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                        {TASK_STATUS_LABELS[s.status as keyof typeof TASK_STATUS_LABELS] || s.status}
                      </span>
                      {canModify && editMode && (
                        <button onClick={() => setBlockingIds((p) => p.filter((x) => x !== s.id))} className="text-red-400 hover:text-red-600"><X size={12} /></button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-gray-400 italic mb-3">Brak powiązanych zadań blokujących.</p>
            )}
            {canModify && editMode && (
              <div>
                <input
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-xs bg-white dark:bg-gray-800 mb-1"
                  placeholder="Szukaj zadania..."
                  value={subtaskSearch}
                  onChange={(e) => setSubtaskSearch(e.target.value)}
                />
                {subtaskSearch && (
                  <div className="rounded-md bg-white dark:bg-gray-800 max-h-40 overflow-y-auto">
                    {availableSubtasks.length === 0
                      ? <p className="text-xs text-gray-400 px-3 py-2">Brak wyników.</p>
                      : availableSubtasks.slice(0, 20).map((s) => (
                          <button key={s.id} className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1.5" onClick={() => { setBlockingIds((p) => [...p, s.id]); setSubtaskSearch(""); }}>
                            <Plus size={11} className="text-gray-400 flex-shrink-0" />
                            <span className="text-gray-500 dark:text-gray-400">{s.task_title} / </span>
                            <span className="text-gray-800 dark:text-gray-200">{s.title}</span>
                          </button>
                        ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {err && <p className="text-xs text-red-600">{err}</p>}
        </div>

        {canModify && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-2 flex-shrink-0">
            {editMode ? (
              <>
                <Button size="sm" loading={updateMutation.isPending} disabled={!isDirty} onClick={handleSave}>
                  <Save size={13} className="mr-1" /> Zapisz
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { setForm({ description: revenue.description, notes: revenue.notes, amount: revenue.amount, expected_date: revenue.expected_date }); setBlockingIds(revenue.blocking_subtasks.map((s) => s.id)); setEditMode(false); setErr(null); }}>
                  Anuluj
                </Button>
              </>
            ) : (
              <Button size="sm" variant="secondary" onClick={() => setEditMode(true)}>Edytuj</Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Project multi-select ──────────────────────────────────────────────────

interface ProjectMultiSelectProps {
  projects: Project[];
  selected: string[];
  onChange: (ids: string[]) => void;
}

function ProjectMultiSelect({ projects, selected, onChange }: ProjectMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const sorted = useMemo(
    () => projects.slice().sort((a, b) => naturalSortKey(a.short_name).localeCompare(naturalSortKey(b.short_name), "pl")),
    [projects]
  );
  const filtered = search
    ? sorted.filter(
        (p) =>
          p.short_name.toLowerCase().includes(search.toLowerCase()) ||
          p.name.toLowerCase().includes(search.toLowerCase())
      )
    : sorted;

  const selectedSorted = sorted.filter((p) => selected.includes(p.id));
  const label =
    selected.length === 0
      ? "Wszystkie przedsięwzięcia"
      : selected.length <= 3
      ? selectedSorted.map((p) => p.short_name).join(", ")
      : `${selectedSorted.slice(0, 2).map((p) => p.short_name).join(", ")} (+${selected.length - 2})`;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 min-w-[220px] max-w-sm justify-between"
      >
        <span className="truncate">{label}</span>
        <ChevronDown size={14} className="flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-20 top-full left-0 mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg w-96 flex flex-col max-h-80">
          {/* Search */}
          <div className="p-2 border-b border-gray-100 dark:border-gray-700">
            <input
              autoFocus
              type="search"
              placeholder="Szukaj przedsięwzięcia..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
            />
          </div>

          {/* Quick actions */}
          <div className="flex gap-3 px-3 py-1.5 border-b border-gray-100 dark:border-gray-700">
            <button type="button" className="text-xs text-[var(--color-primary)] hover:underline" onClick={() => onChange([])}>
              Wszystkie
            </button>
            <button
              type="button"
              className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
              onClick={() => onChange(projects.map((p) => p.id))}
            >
              Zaznacz wszystkie
            </button>
            {selected.length > 0 && (
              <span className="text-xs text-gray-400 ml-auto">{selected.length} zaznaczonych</span>
            )}
          </div>

          {/* List */}
          <div className="overflow-y-auto flex-1">
            {filtered.map((p) => {
              const checked = selected.includes(p.id);
              return (
                <label
                  key={p.id}
                  className="flex items-center gap-2.5 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() =>
                      onChange(checked ? selected.filter((id) => id !== p.id) : [...selected, p.id])
                    }
                    className="w-3.5 h-3.5 rounded accent-[var(--color-primary)] flex-shrink-0"
                  />
                  <span className="text-xs font-bold text-[var(--color-primary)] bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] px-1.5 py-0.5 rounded flex-shrink-0">
                    {p.short_name}
                  </span>
                  <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{p.name}</span>
                </label>
              );
            })}
            {filtered.length === 0 && (
              <p className="text-xs text-gray-400 px-3 py-3">Brak wyników dla „{search}"</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sort helpers ────────────────────────────────────────────────────────────

type SortField = "expected_date" | "amount" | "project__short_name" | "description" | "is_auto_generated";
type SortDir = "asc" | "desc";

// ── Project search select ───────────────────────────────────────────────────

interface ProjectSearchSelectProps {
  projects: Project[];
  isLoading: boolean;
  value: string;
  onChange: (id: string) => void;
  placeholder?: string;
}

function ProjectSearchSelect({ projects, isLoading, value, onChange, placeholder = "— wybierz —" }: ProjectSearchSelectProps) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const sorted = useMemo(
    () => [...projects].sort((a, b) => a.short_name.localeCompare(b.short_name, "pl")),
    [projects],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (p) =>
        p.short_name.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q),
    );
  }, [sorted, query]);

  const selected = projects.find((p) => p.id === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  if (isLoading) {
    return (
      <div className="h-9 w-full rounded bg-gray-200 dark:bg-gray-600 animate-pulse" />
    );
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-left flex items-center justify-between gap-2 min-h-[34px]"
        onClick={() => { setOpen((o) => !o); setQuery(""); }}
      >
        <span className={selected ? "text-gray-800 dark:text-gray-200 truncate" : "text-gray-400 dark:text-gray-500"}>
          {selected ? `${selected.short_name} — ${selected.name}` : placeholder}
        </span>
        <ChevronsUpDown size={14} className="flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded shadow-lg">
          <div className="p-1.5 border-b border-gray-100 dark:border-gray-700">
            <input
              autoFocus
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 outline-none focus:ring-1 focus:ring-[var(--color-primary)]"
              placeholder="Szukaj..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <ul className="max-h-56 overflow-y-auto">
            <li>
              <button
                type="button"
                className="w-full text-left px-3 py-1.5 text-sm text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                onClick={() => { onChange(""); setOpen(false); setQuery(""); }}
              >
                {placeholder}
              </button>
            </li>
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400 dark:text-gray-500">Brak wyników</li>
            )}
            {filtered.map((p) => (
              <li key={p.id}>
                <button
                  type="button"
                  className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-700/50 ${p.id === value ? "bg-[var(--color-primary-dim)] text-[var(--color-primary)] font-medium" : "text-gray-700 dark:text-gray-300"}`}
                  onClick={() => { onChange(p.id); setOpen(false); setQuery(""); }}
                >
                  <span className="font-medium">{p.short_name}</span>
                  <span className="text-gray-400 dark:text-gray-500 ml-1">— {p.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────

const emptyCreateForm = { description: "", amount: "", expected_date: "" };

export function PlannedRevenuesPage() {
  const user = useAuthStore((s) => s.user);
  const canEdit = user?.role === "director";

  // ── Filter + sort + pagination state ──────────────────────────────────
  const [projectFilters, setProjectFilters] = useState<string[]>([]);
  const [typeFilter, setTypeFilter] = useState<"" | "true" | "false">("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortField, setSortField] = useState<SortField>("expected_date");
  const [sortDir, setSortDir] = useState<SortDir>("asc");
  const [page, setPage] = useState(1);

  // Reset to page 1 when filters/sort change
  const resetPage = useCallback(() => setPage(1), []);
  const setProjectFiltersAndReset = useCallback((v: string[]) => { setProjectFilters(v); resetPage(); }, [resetPage]);
  const setTypeFilterAndReset = useCallback((v: "" | "true" | "false") => { setTypeFilter(v); resetPage(); }, [resetPage]);
  const setDateFromAndReset = useCallback((v: string) => { setDateFrom(v); resetPage(); }, [resetPage]);
  const setDateToAndReset = useCallback((v: string) => { setDateTo(v); resetPage(); }, [resetPage]);

  const ordering = sortDir === "desc" ? `-${sortField}` : sortField;

  // ── Form state ────────────────────────────────────────────────────────
  const [createProject, setCreateProject] = useState<string>("");
  const [selectedRevenue, setSelectedRevenue] = useState<PlannedRevenue | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm);
  const [createErr, setCreateErr] = useState<string | null>(null);
  const [tableConvertDialog, setTableConvertDialog] = useState<{ revenueId: string; isAdvance: boolean } | null>(null);

  const { data: projects = [], isLoading: projectsLoading } = useQuery<Project[]>({
    queryKey: ["projects", "list-brief"],
    queryFn: () => apiClient.get<{ data: Project[] }>("/projects/").then((r) => r.data.data),
  });

  // Build query params for the server
  const queryParams = useMemo(() => {
    const p: Record<string, string | number> = { page, ordering };
    if (projectFilters.length === 1) p.project = projectFilters[0];
    if (typeFilter !== "") p.is_auto_generated = typeFilter;
    if (dateFrom) p.date_from = dateFrom;
    if (dateTo) p.date_to = dateTo;
    return p;
  }, [page, ordering, projectFilters, typeFilter, dateFrom, dateTo]);

  const { data: pageData, isLoading } = usePaginatedPlannedRevenues(queryParams);
  const revenues = useMemo(
    () => (pageData?.items ?? []).filter((r) => !r.draft_invoice_id || r.draft_invoice_payment_status !== "paid"),
    [pageData]
  );
  const meta = pageData?.meta;

  const createMutation = useCreatePlannedRevenue(createProject);
  const deleteMutation = useDeletePlannedRevenue();
  const convertMutation = useConvertPlannedRevenueToDraft();

  // Keep selected revenue in sync with latest data
  const liveSelected = selectedRevenue
    ? revenues.find((r) => r.id === selectedRevenue.id) ?? selectedRevenue
    : null;

  // When filters change, close the detail panel
  useEffect(() => { setSelectedRevenue(null); }, [projectFilters, typeFilter, dateFrom, dateTo]);

  // Sort handler — toggle dir if same column, reset to asc on new column
  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("asc");
    }
    resetPage();
  }

  function SortIcon({ col }: { col: SortField }) {
    if (sortField !== col) return <ChevronsUpDown size={11} className="ml-1 opacity-30" />;
    return sortDir === "asc"
      ? <ChevronUp size={11} className="ml-1 text-[var(--color-primary)]" />
      : <ChevronDown size={11} className="ml-1 text-[var(--color-primary)]" />;
  }

  function ThSort({ col, label, cls }: { col: SortField; label: string; cls?: string }) {
    return (
      <th
        className={`text-left px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200 ${cls ?? ""}`}
        onClick={() => handleSort(col)}
      >
        <span className="inline-flex items-center">{label}<SortIcon col={col} /></span>
      </th>
    );
  }

  const handleCreate = async () => {
    if (!createProject) { setCreateErr("Wybierz przedsięwzięcie."); return; }
    if (!createForm.description.trim() || !createForm.amount || !createForm.expected_date) {
      setCreateErr("Opis, kwota i data są wymagane.");
      return;
    }
    try {
      await createMutation.mutateAsync({ project: createProject, ...createForm });
      setCreateForm(emptyCreateForm);
      setCreateProject("");
      setShowCreateForm(false);
      setCreateErr(null);
    } catch { setCreateErr("Błąd zapisu."); }
  };

  const handleDelete = (id: string, isAuto: boolean) => {
    if (isAuto) { alert("Nie można usunąć automatycznego przychodu."); return; }
    if (!confirm("Usunąć planowany przychód?")) return;
    deleteMutation.mutate(id);
  };

  const inputCls = "border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)]";

  if (isLoading && !pageData) return <PageLoader />;

  return (
    <div>
      {liveSelected && (
        <RevenueDetailPanel
          revenue={liveSelected}
          projectId={liveSelected.project}
          canEdit={canEdit}
          onClose={() => setSelectedRevenue(null)}
        />
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-6">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Planowane przychody</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          {meta && (
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {meta.count} {meta.count === 1 ? "pozycja" : "pozycji"}
              {" · łącznie "}
              <span className="font-semibold text-green-700">+{formatAmount(meta.total_amount)} PLN</span>
              {meta.total_pages > 1 && (
                <span className="ml-2 text-gray-400">· strona {meta.page} z {meta.total_pages}</span>
              )}
            </p>
          )}
        </div>
        {canEdit && (
          <Button size="sm" className="w-full sm:w-auto" onClick={() => setShowCreateForm(!showCreateForm)}>
            {showCreateForm ? "Anuluj" : "+ Dodaj planowany przychód"}
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:flex sm:items-center gap-3 mb-4 sm:flex-wrap">
        <ProjectMultiSelect
          projects={projects}
          selected={projectFilters}
          onChange={setProjectFiltersAndReset}
        />

        {/* Type filter */}
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilterAndReset(e.target.value as "" | "true" | "false")}
          className={`${inputCls} w-full sm:w-auto`}
        >
          <option value="">Wszystkie typy</option>
          <option value="false">Ręczne</option>
          <option value="true">Auto</option>
        </select>

        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Od</span>
          <DatePicker
            value={dateFrom}
            onChange={setDateFromAndReset}
            className="flex-1 sm:flex-none min-w-0"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Do</span>
          <DatePicker
            value={dateTo}
            onChange={setDateToAndReset}
            className="flex-1 sm:flex-none min-w-0"
          />
        </div>

        {/* Clear filters */}
        {(projectFilters.length > 0 || typeFilter !== "" || dateFrom || dateTo) && (
          <button
            onClick={() => { setProjectFiltersAndReset([]); setTypeFilterAndReset(""); setDateFromAndReset(""); setDateToAndReset(""); }}
            className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 underline"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {/* Create form */}
      {showCreateForm && (
        <div className="bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Przedsięwzięcie *</label>
            <ProjectSearchSelect
              projects={projects}
              isLoading={projectsLoading}
              value={createProject}
              onChange={setCreateProject}
            />
          </div>
          <div className="col-span-1">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis *</label>
            <input
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={createForm.description}
              onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
              placeholder="np. Faktura częściowa"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Kwota netto (PLN) *</label>
            <input
              type="number" step="0.01"
              className="w-full border border-gray-300 dark:border-gray-600 rounded px-2 py-1.5 text-sm bg-white dark:bg-gray-800"
              value={createForm.amount}
              onChange={(e) => setCreateForm({ ...createForm, amount: e.target.value })}
              placeholder="0.00"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300 block mb-1">Data *</label>
            <DatePicker
              value={createForm.expected_date}
              onChange={(v) => setCreateForm({ ...createForm, expected_date: v })}
            />
          </div>
          {createErr && <p className="col-span-4 text-xs text-red-600">{createErr}</p>}
          <div className="col-span-4 flex gap-2">
            <Button size="sm" loading={createMutation.isPending} onClick={handleCreate}>Zapisz</Button>
            <Button size="sm" variant="secondary" onClick={() => { setShowCreateForm(false); setCreateErr(null); setCreateProject(""); setCreateForm(emptyCreateForm); }}>Anuluj</Button>
          </div>
        </div>
      )}

      {/* Table */}
      {!isLoading && revenues.length === 0 ? (
        <div className="text-center py-16 text-gray-400 dark:text-gray-500">
          <p className="text-sm">Brak planowanych przychodów.</p>
        </div>
      ) : (
        <>
        {/* Mobile cards */}
        <div className="md:hidden space-y-2">
          {revenues.map((r) => (
            <div
              key={r.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50"
              onClick={() => setSelectedRevenue(r)}
            >
              <div className="flex items-start justify-between gap-2 mb-1.5">
                <div className="flex items-center gap-1.5 min-w-0 flex-1">
                  <span className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{r.description}</span>
                  {r.draft_invoice_id && <FileText size={13} className="text-[var(--color-primary)] flex-shrink-0" />}
                  {r.is_blocked && !r.is_auto_generated && <Lock size={13} className="text-amber-500 flex-shrink-0" />}
                </div>
                <Badge color={r.is_auto_generated ? "blue" : "green"}>
                  {r.is_auto_generated ? "Auto" : "Ręczny"}
                </Badge>
              </div>

              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1.5">
                <span className="font-medium text-gray-800 dark:text-gray-200">[{r.project_short_name}]</span>{" "}
                <span className="line-clamp-2">{r.project_name}</span>
              </div>

              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="text-gray-500 dark:text-gray-400 whitespace-nowrap">{formatDate(r.expected_date)}</span>
                {r.draft_invoice_payment_status === "partially_paid" && r.draft_invoice_remaining_net ? (
                  <span className="font-semibold text-amber-700 whitespace-nowrap">
                    +{formatAmount(r.draft_invoice_remaining_net)} PLN
                  </span>
                ) : (
                  <span className="font-semibold text-green-700 whitespace-nowrap">+{formatAmount(r.amount)} PLN</span>
                )}
              </div>

              {r.blocking_subtasks.length > 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {r.blocking_subtasks.filter((s) => s.status !== "done").length > 0
                    ? `${r.blocking_subtasks.filter((s) => s.status !== "done").length} nieukończ. zadań`
                    : <span className="text-green-600">Wszystkie zadania ukończone</span>}
                </p>
              )}

              <div className="mt-2 flex items-center justify-between gap-2" onClick={(e) => e.stopPropagation()}>
                {r.draft_invoice_id ? (
                  <Link
                    to={`/finances/invoices-prz/${r.draft_invoice_id}`}
                    className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${
                      r.draft_invoice_payment_status === "partially_paid"
                        ? "bg-amber-100 text-amber-700"
                        : "bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                    }`}
                  >
                    <FileText size={11} />
                    {r.draft_invoice_payment_status === "partially_paid" ? "Częściowo opłacona" : "Faktura PRZ"}
                  </Link>
                ) : canEdit && !r.is_auto_generated ? (
                  <button
                    disabled={convertMutation.isPending}
                    onClick={() => setTableConvertDialog({ revenueId: r.id, isAdvance: false })}
                    className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] border border-[var(--color-primary)] rounded px-2 py-1 disabled:opacity-50"
                  >
                    <FileText size={11} /> Szkic PRZ
                  </button>
                ) : r.is_blocked && !r.is_auto_generated ? (
                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Zablokowany</span>
                ) : <span />}
                {canEdit && !r.is_auto_generated && (
                  <button
                    onClick={() => handleDelete(r.id, r.is_auto_generated)}
                    className="text-xs text-red-500 hover:underline px-2 py-1"
                  >
                    Usuń
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="hidden md:block bg-white dark:bg-gray-800 overflow-x-auto">
          <table className="w-full text-xs min-w-[700px]">
            <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
              <tr>
                <ThSort col="description" label="Opis" />
                <ThSort col="project__short_name" label="Przedsięwzięcie" />
                <ThSort col="expected_date" label="Data" />
                <th className="text-right px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400 cursor-pointer select-none hover:text-gray-800 dark:hover:text-gray-200" onClick={() => handleSort("amount")}>
                  <span className="inline-flex items-center justify-end">Kwota netto (PLN)<SortIcon col="amount" /></span>
                </th>
                <ThSort col="is_auto_generated" label="Typ" cls="text-center" />
                <th className="text-center px-4 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Status</th>
                {canEdit && <th className="px-4 py-2 w-16 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400" />}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {revenues.map((r) => (
                <tr
                  key={r.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  onClick={() => setSelectedRevenue(r)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-800 dark:text-gray-200 font-medium">{r.description}</span>
                      {r.draft_invoice_id && <span title="Powiązana faktura PRZ"><FileText size={13} className="text-[var(--color-primary)] flex-shrink-0" /></span>}
                      {r.is_blocked && !r.is_auto_generated && <span title="Zablokowany przez niezakończone zadania"><Lock size={13} className="text-amber-500 flex-shrink-0" /></span>}
                    </div>
                    {r.notes && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 truncate max-w-xs">{r.notes}</p>}
                    {r.blocking_subtasks.length > 0 && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {r.blocking_subtasks.filter((s) => s.status !== "done").length > 0
                          ? `${r.blocking_subtasks.filter((s) => s.status !== "done").length} nieukończ. zadań`
                          : <span className="text-green-600">Wszystkie zadania ukończone</span>}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                    <span className="font-medium text-gray-800 dark:text-gray-200 mr-1">[{r.project_short_name}]</span>{r.project_name}
                  </td>
                  <td className="px-4 py-3 text-gray-500 dark:text-gray-400 text-xs whitespace-nowrap">{formatDate(r.expected_date)}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {r.draft_invoice_payment_status === "partially_paid" && r.draft_invoice_remaining_net ? (
                      <div>
                        <span className="font-semibold text-amber-700">
                          +{formatAmount(r.draft_invoice_remaining_net)} PLN
                        </span>
                        <div className="text-xs text-amber-600 mt-0.5">pozostało (częściowo opłacona)</div>
                      </div>
                    ) : (
                      <span className="font-semibold text-green-700">+{formatAmount(r.amount)} PLN</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <Badge color={r.is_auto_generated ? "blue" : "green"}>
                      {r.is_auto_generated ? "Auto" : "Ręczny"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                    {r.draft_invoice_id ? (
                      <Link
                        to={`/finances/invoices-prz/${r.draft_invoice_id}`}
                        className={`text-xs px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
                          r.draft_invoice_payment_status === "partially_paid"
                            ? "bg-amber-100 text-amber-700 hover:bg-amber-200"
                            : "bg-[var(--color-primary-dim)] text-[var(--color-primary)] hover:bg-[var(--color-primary-glow)]"
                        }`}
                      >
                        <FileText size={11} />
                        {r.draft_invoice_payment_status === "partially_paid" ? "Częściowo opłacona" : "Faktura PRZ"}
                      </Link>
                    ) : canEdit && !r.is_auto_generated ? (
                      <button
                        disabled={convertMutation.isPending}
                        onClick={() => setTableConvertDialog({ revenueId: r.id, isAdvance: false })}
                        className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] hover:text-[var(--color-primary)] border border-[var(--color-primary)] hover:border-[var(--color-primary)] rounded px-1.5 py-0.5 transition-colors disabled:opacity-50"
                      >
                        <FileText size={11} /> Utwórz szkic faktury PRZ
                      </button>
                    ) : r.is_blocked && !r.is_auto_generated ? (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">Zablokowany</span>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 text-xs">—</span>
                    )}
                  </td>
                  {canEdit && (
                    <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleDelete(r.id, r.is_auto_generated)}
                        className={`text-xs hover:underline ${r.is_auto_generated ? "text-gray-300 cursor-not-allowed" : "text-red-500"}`}
                      >
                        Usuń
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      {/* Pagination */}
      {meta && meta.total_pages > 1 && (
        <div className="flex items-center justify-between px-1 mt-4">
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Strona {meta.page} z {meta.total_pages} ({meta.count} pozycji)
          </span>
          <div className="flex items-center gap-2">
            <button
              disabled={meta.page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              <ChevronLeft size={14} /> Poprzednia
            </button>
            <button
              disabled={meta.page >= meta.total_pages}
              onClick={() => setPage((p) => p + 1)}
              className="flex items-center gap-1 px-3 py-1.5 text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Następna <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Convert to draft modal */}
      {tableConvertDialog && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">Utwórz szkic faktury PRZ</h3>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={tableConvertDialog.isAdvance}
                onChange={(e) => setTableConvertDialog({ ...tableConvertDialog, isAdvance: e.target.checked })}
                className="w-4 h-4 accent-[var(--color-primary)]"
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">Faktura zaliczkowa</span>
            </label>
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={convertMutation.isPending}
                onClick={() => {
                  const { revenueId, isAdvance } = tableConvertDialog;
                  setTableConvertDialog(null);
                  convertMutation.mutate({ id: revenueId, isAdvance });
                }}
              >
                Utwórz szkic
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setTableConvertDialog(null)}>Anuluj</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
